import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';
import { forgetPushSubscription } from '../services/push';

const AuthContext = createContext(null);

const COMPANY_KEYS = [
  'company_name', 'company_cif', 'company_address', 'company_city',
  'company_postal_code', 'company_country', 'company_phone',
  'company_email', 'company_web', 'company_logo',
];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Map role → permissions array, loaded from backend
  const [rolePermissionsMap, setRolePermissionsMap] = useState({});
  const [company, setCompany] = useState({});

  const loadRolePermissions = async () => {
    try {
      const res = await api.get('/config/role-permissions');
      const map = {};
      res.data.forEach((r) => { map[r.role] = r.permissions; });
      setRolePermissionsMap(map);
    } catch (_) {
      // non-critical
    }
  };

  const loadCompany = async () => {
    try {
      const res = await api.get('/config/settings');
      const data = res.data || {};
      const c = {};
      COMPANY_KEYS.forEach((k) => { c[k] = data[k] || ''; });
      setCompany(c);
    } catch (_) {
      // non-critical
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      api.get('/auth/me')
        .then((res) => {
          setUser(res.data);
          loadRolePermissions();
          loadCompany();
        })
        .catch(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        })
        .finally(() => setLoading(false));
    } else {
      loadCompany();
      setLoading(false);
    }
  }, []);

  // `remember`: sesion larga y renovable (app movil o "mantener sesion")
  const login = async (email, password, remember = false) => {
    const res = await api.post('/auth/login', { email, password, remember });
    const { access_token, user: userData } = res.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    await loadRolePermissions();
    await loadCompany();
    return userData;
  };

  const logout = async () => {
    // Antes de soltar el token: es el que autoriza a dar de baja las notificaciones
    // de este movil, para que la siguiente persona que lo use no reciba las tuyas
    await forgetPushSubscription();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setRolePermissionsMap({});
  };

  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'manager' || user?.role === 'admin';

  // admin → null (show everything); other roles → their permissions array (or null if not configured)
  const allowedMenuIds = isAdmin
    ? null
    : (rolePermissionsMap[user?.role] ?? null);

  return (
    <AuthContext.Provider value={{
      user, login, logout, loading, isAdmin, isManager,
      allowedMenuIds,
      reloadRolePermissions: loadRolePermissions,
      company,
      reloadCompany: loadCompany,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
